import React, { useMemo, useState, useContext } from 'react';

import { Box, Fade, Paper } from '@material-ui/core';

import {
  FormDoc,
  FormSummary as FormSummaryType,
  MultipleChoiceListDataPoint,
  ChartDataPoint,
  User,
  TextListDataPoint,
} from '@vyce/core/src/types';
import { DeviceContext } from '@vyce/core/src/contexts';

import { FormSummaryTitle } from './components/FormSummaryTitle';
import { FormSummaryPieChart } from './components/FormSummaryPieChart';
import { FormSummaryMultipleChoice } from './components/FormSummaryMultipleChoice';
import { FormSummaryText } from './components/FormSummaryText';
import { FormSummaryTime } from './components/FormSummaryTime';
import { FormSummaryDate } from './components/FormSummaryDate';
import { FormSummaryScale } from './components/FormSummaryScale';
import { FormSummaryQuestionTitle } from './components/FormSummaryQuestionTitle';
import { FormAIAnalyticsDialog } from './components/FormAIAnalyticsDialog';
import { FormAnswerRespondentsDialog } from './components/FormAnswerRespondentsDialog';
import { FormSummaryPoweredByScreen } from './components/FormSummaryPoweredBy';
import { FormSummaryCompletionTable } from './components/FormSummaryCompletionTable';
import { FormSummaryToolbar } from './components/FormSummaryToolbar';
import { FormPrintLayout } from './components/FormPrintLayout';
import { visualizationTextListToDateListPoints } from './utils/formSummaryUtils';
import { buildFormSummaryTitleProps } from './utils/formSummaryTitleProps';
import { downloadFormPdfDocs, formHasPdfDocs, printFormAsPdf } from './utils/formPrintExport';

export const FormSummary: React.FC<{
  form: FormDoc | null;
  formSummary: FormSummaryType | null;
  user: User;
  companyId: string;
  companyLogoUrl?: string | null;
}> = ({ form, formSummary, user, companyId, companyLogoUrl }) => {
  const [openAIAnalyticsDialog, setOpenAIAnalyticsDialog] = useState(false);
  const [respondentsDialog, setRespondentsDialog] = useState<{
    questionId: string;
    answerValue: string;
    answerLabel: string;
  } | null>(null);
  const [isDownloadingDocs, setIsDownloadingDocs] = useState(false);
  const { isMobile } = useContext(DeviceContext);

  const hasPdfDocs = useMemo(() => formHasPdfDocs(form), [form]);

  const handleDownloadDocs = async () => {
    if (!form || isDownloadingDocs) return;
    setIsDownloadingDocs(true);
    try {
      await downloadFormPdfDocs(form);
    } finally {
      setIsDownloadingDocs(false);
    }
  };

  const handleItemClick = (questionId: string) => (answerValue: string, answerLabel: string) => {
    setRespondentsDialog({ questionId, answerValue, answerLabel });
  };

  return (
    <FormPrintLayout
      companyLogoUrl={companyLogoUrl}
      toolbar={
        <FormSummaryToolbar
          isMobile={isMobile}
          onExportPdf={printFormAsPdf}
          onDownloadDocs={hasPdfDocs ? handleDownloadDocs : undefined}
          isDownloadingDocs={isDownloadingDocs}
          onOpenAnalytics={() => setOpenAIAnalyticsDialog(true)}
        />
      }
      footer={
        <>
          <FormAIAnalyticsDialog
            open={openAIAnalyticsDialog}
            onClose={() => {
              setOpenAIAnalyticsDialog(false);
            }}
            selectedFormForAIAnalytics={form}
            user={user}
            companyId={form?.companyId || ''}
          />
          {respondentsDialog && (
            <FormAnswerRespondentsDialog
              open={true}
              onClose={() => setRespondentsDialog(null)}
              companyId={companyId}
              formId={form?.id || ''}
              questionId={respondentsDialog.questionId}
              answerValue={respondentsDialog.answerValue}
              answerLabel={respondentsDialog.answerLabel}
              user={user}
            />
          )}
        </>
      }
    >
      <FormSummaryTitle {...buildFormSummaryTitleProps(form, user, { withResponseMetrics: true })} />

      {formSummary?.questionSummaries?.map(question => (
        <Box key={question.questionId} style={{ width: '100%' }}>
          {question.questionType === 'single_choice' && (
            <FormSummaryPieChart
              data={question.visualization.data as ChartDataPoint[]}
              title={question.questionTitle}
              totalAnswers={question.totalAnswers}
              description={question.questionDescription}
              videoLink={question.questionVideoLink}
              photoUrl={question.questionPhoto?.url || undefined}
              pdfUrl={question.questionPdf?.url || undefined}
              explanation={question.questionExplanation || undefined}
              onItemClick={handleItemClick(String(question.questionId))}
            />
          )}
          {question.questionType === 'multiple_choice' && (
            <FormSummaryMultipleChoice
              data={question.visualization.data as MultipleChoiceListDataPoint[]}
              title={question.questionTitle}
              totalAnswers={question.totalAnswers}
              description={question.questionDescription}
              videoLink={question.questionVideoLink}
              photoUrl={question.questionPhoto?.url || undefined}
              pdfUrl={question.questionPdf?.url || undefined}
              explanation={question.questionExplanation || undefined}
              onItemClick={handleItemClick(String(question.questionId))}
            />
          )}
          {(question.questionType === 'short_text' || question.questionType === 'long_text') && (
            <FormSummaryText
              answers={(question.visualization.data as TextListDataPoint[]) || []}
              title={question.questionTitle}
              totalAnswers={question.totalAnswers}
              description={question.questionDescription}
              videoLink={question.questionVideoLink}
              photoUrl={question.questionPhoto?.url || undefined}
              pdfUrl={question.questionPdf?.url || undefined}
              explanation={question.questionExplanation || undefined}
            />
          )}
          {question.questionType === 'time' && (
            <FormSummaryTime
              data={visualizationTextListToDateListPoints(question.visualization)}
              title={question.questionTitle}
              totalAnswers={question.totalAnswers}
              description={question.questionDescription}
              videoLink={question.questionVideoLink}
              photoUrl={question.questionPhoto?.url || undefined}
              pdfUrl={question.questionPdf?.url || undefined}
              explanation={question.questionExplanation || undefined}
              onItemClick={handleItemClick(String(question.questionId))}
            />
          )}
          {question.questionType === 'date' && (
            <FormSummaryDate
              data={visualizationTextListToDateListPoints(question.visualization)}
              title={question.questionTitle}
              totalAnswers={question.totalAnswers}
              description={question.questionDescription}
              videoLink={question.questionVideoLink}
              photoUrl={question.questionPhoto?.url || undefined}
              pdfUrl={question.questionPdf?.url || undefined}
              explanation={question.questionExplanation || undefined}
              onItemClick={handleItemClick(String(question.questionId))}
            />
          )}
          {question.questionType === 'linear_scale' && (
            <FormSummaryScale
              answers={(question.visualization.data as ChartDataPoint[]) || []}
              title={question.questionTitle}
              totalAnswers={question.totalAnswers}
              description={question.questionDescription}
              videoLink={question.questionVideoLink}
              photoUrl={question.questionPhoto?.url || undefined}
              pdfUrl={question.questionPdf?.url || undefined}
              explanation={question.questionExplanation || undefined}
              onItemClick={handleItemClick(String(question.questionId))}
            />
          )}
          {question.questionType === 'no_answer' && (
            <Fade in={true} timeout={200} style={{ width: '100%' }}>
              <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
                <FormSummaryQuestionTitle
                  title={question.questionTitle}
                  description={question.questionDescription}
                  videoLink={question.questionVideoLink}
                  photoUrl={question.questionPhoto?.url || undefined}
                  pdfUrl={question.questionPdf?.url || undefined}
                  explanation={question.questionExplanation || undefined}
                />
              </Paper>
            </Fade>
          )}
        </Box>
      ))}

      {formSummary?.completionRecords && formSummary.completionRecords.length > 0 && (
        <FormSummaryCompletionTable
          records={formSummary.completionRecords}
          showSignatureColumn={!!form?.signature}
        />
      )}

      <FormSummaryPoweredByScreen />
    </FormPrintLayout>
  );
};
